import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { canAccess } from '@/access/roleAccess'
import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { KpiCard } from '@/components/px/KpiCard'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { selectRoleKey } from '@/features/auth/slices/sessionSlice'
import type { ApiUser } from '@/features/users/types'
import { directoryRetried, fetchUsers } from '@/features/users/usersSlice'
import {
  selectAllUsers,
  selectUsersCache,
  selectUsersError
} from '@/features/users/usersSelectors'
import { formatDate, initialsFor } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store'

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/** The row's org role: the membership the active org carries, else the display role. */
const orgRoleFor = (user: ApiUser, activeOrgId: string | null): string | null => {
  const membership = (user.organisations ?? []).find((org) => org.orgId === activeOrgId)
  return membership?.role ?? user.memberRole ?? null
}

/** Status pill tone: the server's own vocabulary, rendered as-is. */
const statusToneFor = (status: string | null | undefined): 'done' | 'neutral' => {
  if (status === 'ACTIVE') return 'done'
  return 'neutral'
}

/**
 * The people list (mockup 1.1 re-authored, PX-W6.2): `/resources/pool` IS the
 * real directory — `GET /api/v1/{orgId}/users` through the users service, with
 * the loading / 403 / error / empty triad (INV-9) and every row linking to
 * the real person profile. Filters are LOCAL UI state over the fetched rows;
 * the KPI strip derives from server rows only.
 */
export default function ResourcePoolPage() {
  const { t } = useTranslation(['people', 'common'])
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const rows = useAppSelector(selectAllUsers)
  const cache = useAppSelector(selectUsersCache)
  const error = useAppSelector(selectUsersError)
  const activeOrgId = useAppSelector((s) => s.session.activeOrgId)
  const roleKey = useAppSelector(selectRoleKey)

  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    void dispatch(fetchUsers())
  }, [dispatch])

  /** Distinct org roles from the server rows — the filter never invents one. */
  const roleOptions = useMemo(() => {
    const roles = new Set<string>()
    for (const user of rows) {
      const role = orgRoleFor(user, activeOrgId)
      if (role !== null && role !== '') roles.add(role)
    }
    return [...roles].sort()
  }, [rows, activeOrgId])

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return rows.filter((user) => {
      if (roleFilter !== 'all' && orgRoleFor(user, activeOrgId) !== roleFilter) return false
      if (statusFilter !== 'all' && (user.memberStatus ?? '') !== statusFilter) return false
      if (needle !== '') {
        const haystack = `${user.name ?? ''} ${user.email}`.toLowerCase()
        if (!haystack.includes(needle)) return false
      }
      return true
    })
  }, [rows, roleFilter, statusFilter, search, activeOrgId])

  const activeCount = useMemo(
    () => rows.filter((user) => user.memberStatus === 'ACTIVE').length,
    [rows]
  )

  const onRetryClicked = (): void => {
    dispatch(directoryRetried())
    void dispatch(fetchUsers())
  }

  const onClearFiltersClicked = (): void => {
    setRoleFilter('all')
    setStatusFilter('all')
    setSearch('')
  }

  const onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value)
  }

  const canInvite = roleKey !== null && canAccess(roleKey, '/admin/people')
  const showEmptyState = !cache.loading && error === null && rows.length === 0

  const onInviteFromEmptyClicked = (): void => {
    navigate('/admin/people')
  }

  return (
    <AppShell
      moduleLabel={t('people:chrome.module')}
      breadcrumb={<span>{t('people:title')}</span>}
      contextPill={t('people:chrome.contextPill')}
      nav={resourcesNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('people:title')}</h1>
        <div className="flex flex-wrap items-baseline gap-x-4">
          <p className="text-sm text-muted-foreground">{t('people:lede')}</p>
          {canInvite && (
            <Link
              to="/admin/people"
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {t('people:inviteCta')}
            </Link>
          )}
        </div>
      </header>

      <div className="mt-4">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/users" />
        ) : error !== null ? (
          <ErrorPanel error={error} onRetryClicked={onRetryClicked} />
        ) : showEmptyState ? (
          <EmptyPanel
            title={t('people:empty.title')}
            body={t('people:empty.body')}
            actionLabel={canInvite ? t('people:empty.action') : undefined}
            onActionClicked={canInvite ? onInviteFromEmptyClicked : undefined}
          />
        ) : (
          <>
            <section aria-label={t('people:filters.search')} className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className="flex items-center gap-1.5 text-sm text-ink-soft">
                {t('people:filters.search')}
                <input
                  type="search"
                  value={search}
                  onChange={onSearchChanged}
                  className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
                />
              </label>
              <label className="flex items-center gap-1.5 text-sm text-ink-soft">
                {t('people:filters.role')}
                <select
                  value={roleFilter}
                  onChange={(event) => setRoleFilter(event.target.value)}
                  className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
                >
                  <option value="all">{t('people:filters.roleAll')}</option>
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-sm text-ink-soft">
                {t('people:filters.status')}
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
                >
                  <option value="all">{t('people:filters.statusAll')}</option>
                  <option value="ACTIVE">{t('people:status.ACTIVE')}</option>
                  <option value="INACTIVE">{t('people:status.INACTIVE')}</option>
                </select>
              </label>
              <button
                type="button"
                onClick={onClearFiltersClicked}
                className="rounded-full border border-border bg-card px-3 py-1 text-sm text-ink-soft hover:bg-muted"
              >
                {t('people:filters.clear')}
              </button>
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                {t('people:resultCount', { shown: filtered.length, total: rows.length })}
              </span>
            </section>

            <section className="mt-4 rounded-lg border border-border bg-card">
              {filtered.length === 0 ? (
                <EmptyPanel
                  title={t('people:empty.title')}
                  body={t('people:empty.body')}
                  actionLabel={t('people:filters.clear')}
                  onActionClicked={onClearFiltersClicked}
                />
              ) : (
                <Table>
                  <TableCaption className="sr-only">{t('people:title')}</TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={columnHeadClass}>{t('people:columns.person')}</TableHead>
                      <TableHead className={columnHeadClass}>{t('people:columns.orgRole')}</TableHead>
                      <TableHead className={columnHeadClass}>{t('people:columns.status')}</TableHead>
                      <TableHead className={columnHeadClass}>{t('people:columns.lastActive')}</TableHead>
                      <TableHead className={columnHeadClass}>{t('people:columns.joined')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((user) => {
                      const orgRole = orgRoleFor(user, activeOrgId)
                      const status = user.memberStatus ?? null
                      return (
                        <TableRow key={user.id}>
                          <TableCell className="py-2.5">
                            <div className="flex items-center gap-2.5">
                              <Avatar size="sm">
                                <AvatarFallback className="bg-people-bg font-semibold text-people-ink">
                                  {initialsFor(user.name ?? user.email)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="leading-tight">
                                <Link
                                  to={`/resources/people/${user.id}`}
                                  className="block font-medium text-foreground underline-offset-2 hover:underline"
                                >
                                  {user.name ?? user.email}
                                </Link>
                                <span className="block text-xs text-muted-foreground">{user.email}</span>
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {orgRole !== null ? (
                              <StatusPill tone="proposed">{orgRole}</StatusPill>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                {t('common:values.unknown')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusPill tone={statusToneFor(status)}>
                              {status !== null
                                ? t(`people:status.${status}`, {
                                    defaultValue: status
                                  })
                                : t('people:status.unknown')}
                            </StatusPill>
                          </TableCell>
                          <TableCell className="text-sm tabular-nums text-foreground">
                            {formatDate(user.lastActiveAt) ?? t('common:values.never')}
                          </TableCell>
                          <TableCell className="text-sm tabular-nums text-foreground">
                            {formatDate(user.joinedAt) ?? t('common:values.unknown')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </section>

            <div className="mt-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
              <KpiCard
                label={t('people:kpis.total')}
                figure={rows.length}
                qualifier={`${rows.length} ${t('people:kpis.total').toLowerCase()}`}
              />
              <KpiCard
                label={t('people:kpis.active')}
                figure={activeCount}
                qualifier={`${rows.length} ${t('people:kpis.total').toLowerCase()}`}
              />
            </div>
          </>
        )}
      </div>

      <SectionLabel className="mt-6">{t('people:chrome.contextPill')}</SectionLabel>
    </AppShell>
  )
}
